import { get, isEmpty, isUndefined } from "lodash";
import { User } from "types/user.type";
import { CompanyFormValue } from "./components/ConfigShareRecordModal";
export const insertLinkToText = (text: string) => {
  if (!text) return "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(text, "text/html");

  const urlRegex =
    /(?<!href=["'])((?:http|https|ftp|ftps):\/\/[a-zA-Z0-9\-.]+\.[a-zA-Z]{2,}(\/\S*)?)/g;

  const replaceTextNode = (node: Node) => {
    if (node.parentElement?.tagName.toLowerCase() === "a") {
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const textContent = node.textContent || "";

      urlRegex.lastIndex = 0;

      if (!urlRegex.test(textContent)) return;

      urlRegex.lastIndex = 0;

      const fragment = document.createDocumentFragment();

      let lastIndex = 0;

      textContent.replace(urlRegex, (match, _p1, offset) => {
        const beforeText = textContent.slice(lastIndex, offset);

        if (beforeText) {
          fragment.appendChild(document.createTextNode(beforeText));
        }

        const link = document.createElement("a");

        link.href = match;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.textContent = match;

        fragment.appendChild(link);

        lastIndex = offset + match.length;

        return match;
      });

      const remaining = textContent.slice(lastIndex);

      if (remaining) {
        fragment.appendChild(document.createTextNode(remaining));
      }

      node.parentNode?.replaceChild(fragment, node);
    } else {
      node.childNodes.forEach(replaceTextNode);
    }
  };

  doc.body.childNodes.forEach(replaceTextNode);
  return doc.body.innerHTML;
};

export const getOwnershipTypeLabel = (
  isNewRecord?: boolean,
  auth?: User | null,
  ownershipType?: string,
  value?: { name?: string; id?: number } | null,
  valueShare?: { name?: string; id?: number } | null,
) => {
  if (isNewRecord) {
    return {
      name: get(auth, "workers[0].full_name", ""),
      id: get(auth, "workers[0].id", undefined),
    };
  } else {
    switch (ownershipType) {
      case "owner":
        return {
          name: get(value, "name", ""),
          id: get(value, "id", undefined),
        };
      case "shared":
        return {
          name: get(valueShare, "name", ""),
          id: get(valueShare, "id", undefined),
        };
      case null:
        return {
          name: "",
          id: undefined,
        };
      default:
        return {
          name: get(value, "name", ""),
          id: get(value, "id", undefined),
        };
    }
  }
};

export const tableDescriptions = {
  view: [
    "・他社へ情報を展開したい場合",
    "・作成側の会社は引き続き<strong>編集／削除</strong>可能",
    "・共有受けた側の会社は<strong>閲覧のみ</strong>で編集不可",
    "・共有受けた側の会社の<strong>削除</strong>は<strong>共有解除</strong>",
  ],
  edit: [
    "・他社とレコードを<strong>共同編集</strong>したい場合",
    "・作成側の会社は引き続き<strong>編集／削除</strong>可能",
    "・共有受けた側の会社は<strong>閲覧／編集</strong>可能",
    "・共有受けた側の会社の<strong>削除</strong>は<strong>共有解除</strong>",
  ],
  copy: [
    "・他社へ別レコードでコピー提供したい場合",
    "・作成側の会社は引き続き<strong>編集／削除</strong>可能で他社への反映／影響は無し",
    "・提供受けた側の会社は受けた時点の別レコードとして保存され、独自に<strong>閲覧／編集／削除</strong>可能",
  ],
};
export const radioConfig = [
  {
    value: "view_anonymous",
    type: "mainSelection",
    shareType: "view",
    dataType: "anonymous",
  },
  {
    value: "view_sharer",
    type: "mainSelection",
    shareType: "view",
    dataType: "shared",
  },
  {
    value: "view_original",
    type: "mainSelection",
    shareType: "view",
    dataType: "owned",
  },
  {
    value: "edit_anonymous",
    type: "mainSelection",
    shareType: "sync_edit",
    dataType: "anonymous",
  },
  {
    value: "edit_sharer",
    type: "mainSelection",
    shareType: "sync_edit",
    dataType: "shared",
  },
  {
    value: "edit_original",
    type: "mainSelection",
    shareType: "sync_edit",
    dataType: "owned",
  },
  {
    value: "copy",
    type: "copySelection",
    shareType: "cloned",
    dataType: "anonymous",
  },
];

export const getOwnerShipType = (ownershipType: string, shareType: string) => {
  const res = radioConfig?.find(
    (item) => item?.shareType === shareType && item?.dataType === ownershipType,
  );
  return !isUndefined(res) ? res : null;
};
export const convertToFormData = (
  oldFormikValues: {
    companies: CompanyFormValue[];
  },
  values: { companies: CompanyFormValue[] },
  appRecordIds: string[],
) => {
  const newData = {
    app_record_ids: appRecordIds,
    company_updates: values.companies?.flatMap((company) => {
      const oldCompany = oldFormikValues.companies?.find(
        (oldComp) => oldComp.id === company.id,
      );

      const results = [];

      if (company?.isPreviousMainSelection && company?.mainSelection === null) {
        const radioItem = radioConfig?.find(
          (radio) => radio.value === oldCompany?.mainSelection,
        );
        results.push({
          company_id: company.id,
          share_type: radioItem?.shareType,
          ownership_type: null,
        });
      } else if (company.mainSelection) {
        const radioItem = radioConfig?.find(
          (radio) => radio.value === company.mainSelection,
        );
        if (radioItem) {
          const oldMainSelection = oldCompany?.mainSelection;
          if (oldMainSelection !== company.mainSelection) {
            results.push({
              company_id: company.id,
              share_type: radioItem.shareType,
              ownership_type: radioItem.dataType,
            });
          }
        }
      }

      if (company.copySelection) {
        const radioItem = radioConfig?.find(
          (radio) => radio.value === company.copySelection,
        );
        if (radioItem) {
          const oldCopySelection = oldCompany?.copySelection;
          if (oldCopySelection !== company.copySelection) {
            results.push({
              company_id: company.id,
              share_type: radioItem.shareType,
              ownership_type: radioItem.dataType,
            });
          }
        }
      }

      return results.filter(
        (obj) =>
          obj.share_type !== undefined &&
          obj.ownership_type !== undefined &&
          !isEmpty(obj),
      );
    }),
  };

  return newData;
};
export const formatCompanyName = (name?: string) => {
  if (!name) return "";
  const removeParentheses = name.replace(
    /\(編集共有なし\)|\(編集共有\)|\(閲覧のみ\)/g,
    "",
  );
  const isJapanese =
    /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u4dbf]/.test(
      removeParentheses,
    );

  if (isJapanese) {
    const convertedText = name
      .replace(/[！-～]/g, (ch) =>
        String.fromCharCode(ch.charCodeAt(0) - 0xfee0),
      )
      .replace(/\u3000/g, " ")
      .replace(/[\u30FC\uFF0D\u2015\u2212]/g, "-");

    const processedText = convertedText
      .replace(/株式会社|有限会社/g, "")
      .trim();

    return processedText.length > 10
      ? processedText.slice(0, 11)
      : processedText;
  }

  return removeParentheses.replace(/株式会社|有限会社/g, "").trim();
};

export const getCanonicalFieldKey = (
  colKey: string | number,
  appParts?: any[],
): string => {
  if (!colKey) return "";
  const rawKey = String(colKey).replace("_attr", "");

  if (rawKey.includes("record:")) return rawKey;

  const type = appParts?.find(
    (item: any) => String(item.id) === rawKey,
  )?.app_part_type;

  if (type === "created_datetime") return "record:created_at";
  if (type === "updated_datetime") return "record:updated_at";
  if (type === "created_by") return "record:created_by";
  if (type === "updated_by") return "record:updated_by";
  if (type === "record_number") return "record:row_id";

  return `app:${rawKey}`;
};

export const getFormikKeyFromCanonical = (
  canonicalKey: string,
  appParts?: any[],
): string => {
  if (canonicalKey.startsWith("app:")) return canonicalKey.replace("app:", "");

  const matchedPart = appParts?.find((item: any) => {
    if (
      canonicalKey === "record:created_at" &&
      item.app_part_type === "created_datetime"
    )
      return true;
    if (
      canonicalKey === "record:updated_at" &&
      item.app_part_type === "updated_datetime"
    )
      return true;
    if (
      canonicalKey === "record:created_by" &&
      item.app_part_type === "created_by"
    )
      return true;
    if (
      canonicalKey === "record:updated_by" &&
      item.app_part_type === "updated_by"
    )
      return true;
    if (
      canonicalKey === "record:row_id" &&
      item.app_part_type === "record_number"
    )
      return true;
    return false;
  });

  if (matchedPart) return String(matchedPart.id);

  return canonicalKey;
};

export const extractFilterValuesForColumn = (
  config: any,
  columnKey?: string,
  filterOptionsData?: any,
  appParts?: any[],
): string[] => {
  let type: string | undefined;
  if (appParts && columnKey) {
    const rawColumnKey = String(columnKey)
      .replace("app:", "")
      .replace("record:", "")
      .replace("_attr", "");
    type = appParts.find(
      (item: any) => String(item.id) === rawColumnKey,
    )?.app_part_type;
  }
  const dateTypes = [
    "date",
    "date_and_time",
    "time",
    "created_at",
    "updated_at",
    "created_datetime",
    "updated_datetime",
  ];

  const rawFilter = config?.filter;
  let values: string[] = [];

  if (Array.isArray(rawFilter)) {
    values = rawFilter.map(String);
  } else if (typeof rawFilter === "object" && rawFilter !== null) {
    values = Object.keys(rawFilter)
      .filter((k) => rawFilter[k] === true)
      .map(String);
  } else if (rawFilter != null) {
    values = [String(rawFilter)];
  }

  const search = config?.search;
  if (search != null && search !== "") {
    let filterOptions = config?.filterOptions || [];

    if (filterOptions.length === 0 && columnKey && filterOptionsData) {
      const formattedKey = getCanonicalFieldKey(columnKey, appParts);
      const optionsArray = filterOptionsData[formattedKey];
      if (Array.isArray(optionsArray)) {
        filterOptions = optionsArray
          .filter((opt: any) => opt.value !== "__NULL__")
          .map((opt: any) => ({
            [columnKey]: { label: opt.label, value: opt.value },
          }));
      }
    }

    const filterOptionsSearch = filterOptions.filter((option: any) => {
      const innerOpt =
        option.label !== undefined ? option : (Object.values(option)[0] as any);
      if (!innerOpt) return false;
      const labelStr = innerOpt.label
        ? String(innerOpt.label).toLowerCase()
        : "";
      const valueStr = innerOpt.value
        ? String(innerOpt.value).toLowerCase()
        : "";

      if (type && dateTypes.includes(type)) {
        // Since we removed formatRawDateToAPI import to avoid circular dep, we skip format check here or just use simple search
      }

      const searchStr = search.toString().trim().toLowerCase();
      return labelStr.includes(searchStr) || valueStr.includes(searchStr);
    });

    const searchValues = filterOptionsSearch.map((option: any) => {
      const innerOpt =
        option.label !== undefined ? option : (Object.values(option)[0] as any);
      return String(innerOpt?.value);
    });

    if (values.length > 0) {
      const combined = Array.from(new Set([...values, ...searchValues]));
      values = combined.length > 0 ? combined : ["__NOT_FOUND__"];
    } else {
      values = searchValues.length > 0 ? searchValues : ["__NOT_FOUND__"];
    }
  }

  if (config?.filter_special_empty) {
    values.push("__NULL__");
  }

  if (type && dateTypes.includes(type)) {
    values = values.map((val) => {
      if (val === "__NOT_FOUND__" || val === "__NULL__") return val;
      return val;
    });
  }

  return values;
};

export const processCascadingClear = (
  prevOrder: string[],
  formattedKey: string,
  hasFilter: boolean,
  isFilterConditionChanged: boolean,
  appParts: any[],
) => {
  const currentIndex = prevOrder.indexOf(formattedKey);
  let newOrder = [...prevOrder];
  const keysToClear: string[] = [];

  if (currentIndex !== -1 && isFilterConditionChanged) {
    const downstreamKeys = prevOrder.slice(currentIndex + 1);
    downstreamKeys.forEach((k) => {
      const formikKey = getFormikKeyFromCanonical(k, appParts);
      keysToClear.push(formikKey);
    });
    newOrder = newOrder.slice(0, currentIndex + 1);
  } else if (currentIndex === -1 && hasFilter) {
    newOrder.push(formattedKey);
  }

  if (!hasFilter && currentIndex !== -1) {
    const downstreamKeys = prevOrder.slice(currentIndex + 1);
    downstreamKeys.forEach((k) => {
      const formikKey = getFormikKeyFromCanonical(k, appParts);
      keysToClear.push(formikKey);
    });
    newOrder = newOrder.filter((k) => k !== formattedKey);
  }

  return { newOrder, keysToClear };
};
